import { useEffect, useState } from 'react';
import type { Session } from '../App';
import { Container, PageHeader } from '../App';

interface Props { session: Session; }

interface SeasonSummary {
  key: string;
  display_name: string;
  start_ms: number;
  end_ms: number;
}

interface LoginRewardView {
  day: number;
  claimed: boolean;
  current_day: boolean;
}

interface BattlePassTierView {
  tier: number;
  unlocked: boolean;
  claimed: boolean;
}

interface BattlePassView {
  current_tier: number;
  max_tier: number;
  current_xp: number;
  xp_per_tier: number;
  premium: boolean;
  tiers: BattlePassTierView[];
}

export default function LiveOps({ session }: Props) {
  const playerId = session.playerId;
  const [seasons, setSeasons] = useState<SeasonSummary[]>([]);
  const [calendar, setCalendar] = useState<LoginRewardView[]>([]);
  const [streak, setStreak] = useState(0);
  const [battlePass, setBattlePass] = useState<BattlePassView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Form state
  const [seasonId, setSeasonId] = useState('');
  const [claimDay, setClaimDay] = useState('');
  const [xpAmount, setXpAmount] = useState('100');

  const base = `/api/v1/games/${session.gameId}/modules/liveops`;

  const reloadSeasons = () => {
    fetch(`${base}/seasons`)
      .then(r => r.json())
      .then((d: { seasons: SeasonSummary[] }) => {
        setSeasons(d.seasons);
        if (d.seasons.length > 0 && !seasonId) setSeasonId(d.seasons[0].key);
      })
      .catch(e => setError(e.message));
  };

  const reloadLogin = () => {
    fetch(`${base}/player/${playerId}/login`)
      .then(r => r.json())
      .then((d: { calendar: LoginRewardView[]; streak: number }) => {
        setCalendar(d.calendar);
        setStreak(d.streak);
      })
      .catch(e => setError(e.message));
  };

  const reloadBp = () => {
    fetch(`${base}/player/${playerId}/battle-pass`)
      .then(r => r.json())
      .then((d: { battle_pass: BattlePassView }) => setBattlePass(d.battle_pass))
      .catch(e => setError(e.message));
  };

  useEffect(() => { reloadSeasons(); reloadLogin(); reloadBp(); }, [session.gameId, playerId]);

  const claimLogin = async () => {
    if (!seasonId || !claimDay) return;
    setBusy(true); setError(null);
    try {
      const res = await fetch(`${base}/player/${playerId}/login/claim`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ season_id: seasonId, day: Number(claimDay) }),
      });
      if (!res.ok) throw new Error(await res.text());
      setClaimDay('');
      reloadLogin();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const addXp = async () => {
    if (!seasonId || !xpAmount) return;
    setBusy(true); setError(null);
    try {
      const res = await fetch(`${base}/player/${playerId}/battle-pass/xp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ season_id: seasonId, xp: Number(xpAmount) }),
      });
      if (!res.ok) throw new Error(await res.text());
      reloadBp();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const xpPct = battlePass && battlePass.xp_per_tier > 0
    ? Math.min(100, Math.round(((battlePass.current_xp % battlePass.xp_per_tier) / battlePass.xp_per_tier) * 100))
    : 0;

  return (
    <Container>
      <PageHeader
        title="LiveOps"
        subtitle="Seasons, login calendar, and battle pass."
      />
      {error && <div className="error-banner">{error}</div>}

      {/* Season List */}
      {seasons.length > 0 && (
        <div className="card mb-5 p-4">
          <h3 className="text-sm font-semibold mb-3">Seasons</h3>
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Key</th><th>Name</th><th>Start</th><th>End</th></tr>
              </thead>
              <tbody>
                {seasons.map(s => (
                  <tr key={s.key}>
                    <td className="font-mono text-xs">{s.key}</td>
                    <td>{s.display_name}</td>
                    <td className="text-xs">{new Date(s.start_ms).toLocaleString()}</td>
                    <td className="text-xs">{new Date(s.end_ms).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Login Calendar Claim */}
      <div className="card mb-5 p-4">
        <h3 className="text-sm font-semibold mb-3">Login Calendar (streak: {streak})</h3>
        {calendar.length > 0 && (
          <div className="flex gap-2 flex-wrap mb-3">
            {calendar.map(c => (
              <div key={c.day} className={`w-10 h-10 rounded-md flex items-center justify-center text-xs font-medium border ${c.claimed ? 'bg-brand text-white border-brand' : c.current_day ? 'border-brand text-brand' : 'border-line text-fg-muted'}`}>
                D{c.day}
              </div>
            ))}
          </div>
        )}
        <div className="flex gap-3 items-end">
          <div>
            <label className="text-xs text-fg-muted block mb-1">Season ID</label>
            <input className="input" placeholder="s1" value={seasonId} onChange={e => setSeasonId(e.target.value)} />
          </div>
          <div>
            <label className="text-xs text-fg-muted block mb-1">Day</label>
            <input className="input w-20" type="number" placeholder="1" value={claimDay} onChange={e => setClaimDay(e.target.value)} />
          </div>
          <button className="btn btn-primary" disabled={busy || !seasonId || !claimDay} onClick={claimLogin}>Claim</button>
        </div>
      </div>

      {/* Battle Pass */}
      <div className="card mb-5 p-4">
        <h3 className="text-sm font-semibold mb-3">Battle Pass</h3>
        {battlePass && (
          <>
            <div className="flex items-center gap-3 mb-3">
              <span className="text-xs text-fg-muted">Tier {battlePass.current_tier}/{battlePass.max_tier}</span>
              <div className="flex-1 h-2 rounded-full bg-bg-2 overflow-hidden">
                <div className="h-full rounded-full bg-brand transition-all" style={{ width: `${xpPct}%` }} />
              </div>
              <span className="text-xs text-fg-muted">{battlePass.current_xp} XP</span>
              {battlePass.premium && <span className="badge badge-ok">Premium</span>}
            </div>
            {battlePass.tiers.length > 0 && (
              <div className="flex gap-2 flex-wrap mb-3">
                {battlePass.tiers.map(t => (
                  <div key={t.tier} className={`w-10 h-10 rounded-md flex items-center justify-center text-xs font-medium border ${t.claimed ? 'bg-brand text-white border-brand' : t.unlocked ? 'border-brand text-brand' : 'border-line text-fg-muted'}`}>
                    T{t.tier}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
        <div className="flex gap-3 items-end">
          <div>
            <label className="text-xs text-fg-muted block mb-1">Season ID</label>
            <input className="input" placeholder="s1" value={seasonId} onChange={e => setSeasonId(e.target.value)} />
          </div>
          <div>
            <label className="text-xs text-fg-muted block mb-1">XP</label>
            <input className="input w-20" type="number" value={xpAmount} onChange={e => setXpAmount(e.target.value)} />
          </div>
          <button className="btn btn-primary" disabled={busy || !seasonId} onClick={addXp}>Add XP</button>
        </div>
      </div>

      {seasons.length === 0 && (
        <div className="empty">
          <div className="text-fg-2 font-medium mb-1">No seasons published</div>
          <div className="text-xs text-fg-muted">Create a season in Config Explorer, publish, then manage here.</div>
        </div>
      )}
    </Container>
  );
}
